import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { CategoryBadge, StatusBadge, FeaturedBadge } from '../ui/Badge';
import { CompactCountdown } from './CountdownTimer';
import ProgressBar from './ProgressBar';
import { HeatMeterBadge } from './HeatMeter';
import { PriceDisplay } from '../ui/PriceInput';
import LocationBadge from '../shared/LocationBadge';

/**
 * RaffleCard - Lucky Number Design System
 *
 * Dark-first design with subtle borders and teal accents.
 * Hover lift effect for interactivity.
 */
export default function RaffleCard({
  raffle,
  featured = false,
  className,
}) {
  const {
    id,
    title,
    photos,
    category,
    status,
    ticketPrice,
    ticketQuantity,
    ticketsSold = 0,
    pickupArea,
    endsAt,
  } = raffle;

  const isActive = status === 'active';
  const isSoldOut = ticketsSold >= ticketQuantity;

  return (
    <Link
      to={`/competition/${id}`}
      className={cn(
        'group block relative',
        'bg-midnight-light border border-midnight-lighter rounded-lg overflow-hidden',
        'transition-all duration-300 ease-smooth',
        'hover:border-primary/30 hover:-translate-y-1 md:hover:-translate-y-2',
        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-midnight',
        'animate-card-enter',
        className
      )}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-midnight-lighter">
        {photos?.[0] ? (
          <img
            src={photos[0]}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-cream/30">
            <PlaceholderIcon className="w-8 h-8 md:w-12 md:h-12" />
          </div>
        )}

        {/* Featured badge or Heat meter */}
        {featured ? (
          <div className="absolute top-2 left-2 md:top-3 md:left-3">
            <FeaturedBadge />
          </div>
        ) : isActive && (
          <HeatMeterBadge raffle={raffle} />
        )}

        {/* Status overlay */}
        {!isActive && (
          <div className="absolute inset-0 bg-midnight/60 flex items-center justify-center">
            <StatusBadge status={status} className="text-xs md:text-sm" />
          </div>
        )}

        {/* Sold out overlay */}
        {isActive && isSoldOut && (
          <div className="absolute inset-0 bg-midnight/60 flex items-center justify-center">
            <span className="px-2 py-1 md:px-4 md:py-2 bg-error text-white font-bold uppercase text-xs md:text-sm rounded-full">
              Sold Out
            </span>
          </div>
        )}

        {/* Timer overlay on mobile */}
        {isActive && endsAt && (
          <div className="absolute bottom-2 right-2 md:hidden">
            <CompactCountdown endDate={endsAt} size="sm" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 md:p-4">
        {/* Top row: Category & Timer */}
        <div className="flex items-center justify-between gap-2 mb-1.5 md:mb-2">
          <CategoryBadge category={category} size="sm" />
          {isActive && endsAt && (
            <div className="hidden md:block">
              <CompactCountdown endDate={endsAt} />
            </div>
          )}
        </div>

        {/* Title */}
        <h3 className="font-semibold text-sm md:text-lg text-cream line-clamp-2 mb-1 group-hover:text-primary-light transition-colors">
          {title}
        </h3>

        {/* Location */}
        <div className="mb-2 md:mb-3">
          <LocationBadge location={pickupArea} size="sm" />
        </div>

        {/* Price & Progress */}
        <div className="flex items-center justify-between gap-2 md:gap-4">
          {/* Price */}
          <div>
            <PriceDisplay amount={ticketPrice} cents size="lg" className="text-base md:text-2xl text-primary" />
          </div>

          {/* Tickets progress */}
          <div className="flex-1 max-w-[80px] md:max-w-[120px]">
            <ProgressBar
              sold={ticketsSold}
              total={ticketQuantity}
              size="sm"
              compact
            />
          </div>
        </div>
      </div>
    </Link>
  );
}

/**
 * RaffleCardSkeleton
 *
 * Loading placeholder for RaffleCard with dark theme.
 */
export function RaffleCardSkeleton({ className }) {
  return (
    <div
      className={cn(
        'relative bg-midnight-light border border-midnight-lighter rounded-lg overflow-hidden',
        className
      )}
    >
      {/* Image skeleton */}
      <div className="aspect-[4/3] skeleton" />

      {/* Content skeleton */}
      <div className="p-2 md:p-4 space-y-2 md:space-y-3">
        {/* Title */}
        <div className="space-y-1 md:space-y-2">
          <div className="h-4 md:h-5 skeleton rounded" />
          <div className="h-4 md:h-5 w-3/4 skeleton rounded" />
        </div>

        {/* Price */}
        <div className="flex items-center justify-between">
          <div className="h-5 md:h-7 w-12 md:w-16 skeleton rounded" />
          <div className="h-3 w-10 md:hidden skeleton rounded" />
          <div className="hidden md:block w-24 space-y-1">
            <div className="h-1.5 skeleton rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * RaffleCardGrid
 *
 * Responsive grid layout for raffle cards.
 */
export function RaffleCardGrid({ children, className }) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6',
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * FeaturedRaffleCard
 *
 * Larger card for featured raffles with more details.
 * Dark theme with accent glow on hover.
 */
export function FeaturedRaffleCard({ raffle, className }) {
  const {
    id,
    title,
    description,
    photos,
    category,
    status,
    ticketPrice,
    ticketQuantity,
    ticketsSold = 0,
    pickupArea,
    endsAt,
  } = raffle;

  const isActive = status === 'active';

  return (
    <Link
      to={`/competition/${id}`}
      className={cn(
        'group block bg-midnight-light rounded-xl overflow-hidden',
        'border border-accent/20',
        'transition-all duration-300',
        'hover:border-accent/40 hover:-translate-y-1',
        'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-midnight',
        className
      )}
    >
      <div className="flex flex-col md:flex-row">
        {/* Image */}
        <div className="relative md:w-1/2 aspect-video md:aspect-auto overflow-hidden bg-midnight-lighter">
          {photos?.[0] && (
            <img
              src={photos[0]}
              alt={title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          )}

          {/* Featured badge */}
          <div className="absolute top-4 left-4">
            <FeaturedBadge />
          </div>
        </div>

        {/* Content */}
        <div className="md:w-1/2 p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <CategoryBadge category={category} />
              <StatusBadge status={status} endsAt={endsAt} />
            </div>

            <h3 className="text-2xl font-display text-cream mb-2 group-hover:text-primary-light transition-colors">
              {title}
            </h3>

            <p className="text-cream/60 line-clamp-2 mb-4">
              {description}
            </p>

            <LocationBadge location={pickupArea} size="sm" />
          </div>

          <div className="mt-4 pt-4 border-t border-midnight-lighter">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-baseline gap-1">
                <PriceDisplay amount={ticketPrice} cents size="xl" className="text-primary" />
                <span className="text-cream/50">/entry</span>
              </div>

              {isActive && endsAt && (
                <CompactCountdown endDate={endsAt} size="md" />
              )}
            </div>

            <ProgressBar
              sold={ticketsSold}
              total={ticketQuantity}
              showPercentage
            />
          </div>
        </div>
      </div>
    </Link>
  );
}

// Icon components
function PlaceholderIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

